<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useInfiniteScroll } from '@vueuse/core'
import { useRoute, useRouter } from 'vue-router/composables'
import { historyCommonConfig, historyDeleteModalTheme } from './configs/common'
import HistoryPageDefault from './components/HistoryPage.default.vue'
import HistoryPageFlipnovel from './components/HistoryPage.flipnovel.vue'
import { api } from '@/api'
import { brand } from '@/config/brands'
import { useReadStore, useToastStore, useTrackValueStore } from '@/stores'
import { TRACK_CHANNEL_NAME_VALUE } from '@/constants'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'
import { loadBrandComponent } from '@/utils/loadBrandConfig'

type Book = GetReadHistoryListResponse['data']['records'][0]

const config = historyCommonConfig

const HistoryPage = loadBrandComponent({
  rocnovel: HistoryPageDefault,
  oceread: HistoryPageDefault,
  talewave: HistoryPageDefault,
  ficmoon: HistoryPageDefault,
  novelarc: HistoryPageDefault,
  storyella: HistoryPageDefault,
  silktale: HistoryPageDefault,
  pearla: HistoryPageDefault,
  fictora: HistoryPageDefault,
  marinovel: HistoryPageDefault,
  flipnovel: HistoryPageFlipnovel,
  moonpage: HistoryPageFlipnovel,
  storlyn: HistoryPageFlipnovel,
  novaris: HistoryPageFlipnovel,
})

// flipnovel 组为单 Tab（仅阅读记录）的简化页，由本页负责拉取数据；
// 其余品牌走 HistoryPage.default 的双 Tab（书架 + 阅读记录）自包含实现（内部 useHistoryPage）
const isFlipnovelGroup = ['flipnovel', 'moonpage', 'storlyn', 'novaris'].includes(brand)

const { $toast } = useToastStore()
const readStore = useReadStore()
const route = useRoute()
const router = useRouter()

const from = computed(() => route.query.from as string)

// ===== 以下仅 flipnovel 组使用：阅读记录分页拉取 =====
const history = ref<Book[]>([])
const page = ref(1)
const total = ref<number>(0)
const fetching = ref(false)
const ending = ref(false)
const error = ref<any>()
const pagination = computed(() => ({
  pageIndex: page.value,
  pageSize: 20,
}))

async function fetchHistory() {
  fetching.value = true
  try {
    const { data } = await api.get<any, GetReadHistoryListResponse>('/read/history/list', { params: { ...pagination.value } })
    history.value = [...history.value, ...data.records]
    total.value = data.total
    if (page.value >= data.pages)
      ending.value = true
    else
      page.value += 1
  }
  catch (_error) {
    error.value = _error
  }
  finally {
    fetching.value = false
  }
}

const infiniteRef = ref<HTMLElement>()
function setInfiniteRef(el: HTMLElement | null) {
  infiniteRef.value = el || undefined
}

useInfiniteScroll(infiniteRef,
  async () => {
    if (!isFlipnovelGroup || fetching.value || ending.value || error.value)
      return
    await fetchHistory()
  },
  { distance: 50 },
)

function gotoChapter(item: Book) {
  if (item.offlineFlag)
    return $toast(config.texts.bookRemoved)

  readStore.update({
    contentId: item.contentId,
    chapterId: item.contentChapterId,
  })
  const trackValueStore = useTrackValueStore()
  trackValueStore.set({ bookId: item.contentId, firstOrigin: 4, originName: 4, chlName: TRACK_CHANNEL_NAME_VALUE.READ_HISTORY })

  router.push({
    name: READER_ROUTE_NAME,
    params: {
      contentId: item.contentId,
      chapterId: item.contentChapterId,
    },
  })
}

// 根据品牌组分发不同 props：默认双 Tab 自包含（config + deleteModalTheme）；
// flipnovel 单 Tab 由本页喂数据
const historyPageProps = computed<Record<string, any>>(() =>
  isFlipnovelGroup
    ? {
        config,
        history: history.value,
        fetching: fetching.value,
        ending: ending.value,
        page: page.value,
        from: from.value,
        setInfiniteRef,
        gotoChapter,
      }
    : {
        config,
        deleteModalTheme: historyDeleteModalTheme,
      },
)
</script>

<template>
  <HistoryPage v-bind="historyPageProps" />
</template>
